import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, useMemo } from 'react';
import { Layer, Map, MapRef, Marker, Source } from 'react-map-gl';
import { MapConfig } from 'components/MapboxContainer/util/config';
import MapImage, { directionProps } from 'components/MapboxContainer/RouteControlLayers';
import style from 'styled-components';
import { getRealRoute } from '../api';

import { ReactComponent as MyLocation } from 'constants/icon/m_mylocation.svg';
import { ReactComponent as Start } from 'constants/icon/ic_delivery_start.svg';
import { ReactComponent as End } from 'constants/icon/ic_delivery_end.svg';

import { styled } from '@tanstack/react-query-devtools/build/lib/utils';
import { tempData } from './tempData';

type Props = {
  encryptId: string;
  driverId: number;
  destinationCordinates: number[];
  orderStatus?: string;
};

// lat range: -90 ~ 90
// log range: -180 ~ 180
const getBearing = ({
  lat1,
  lat2,
  lon1,
  lon2,
}: {
  lat1: number | null;
  lon1: number | null;
  lat2: number;
  lon2: number;
}) => {
  // null 시 0으로 처리
  if (lat1 === null || lon1 === null) {
    return 0;
  }
  const toRadians = (deg: number) => deg * (Math.PI / 180);
  const toDegrees = (rad: number) => rad * (180 / Math.PI);

  const pie1 = toRadians(lat1);
  const pie2 = toRadians(lat2);
  const deltaLambda = toRadians(lon2 - lon1);

  const y = Math.sin(deltaLambda) * Math.cos(pie2);
  const x = Math.cos(pie1) * Math.sin(pie2) - Math.sin(pie1) * Math.cos(pie2) * Math.cos(deltaLambda);
  const theta = Math.atan2(y, x);

  // 360로 변환처리
  return (toDegrees(theta) + 360) % 360;
};

export default function DeliveryMap({ encryptId, driverId, destinationCordinates, orderStatus }: Props) {
  // console.log('destinationCordinates: ', destinationCordinates);
  const mapRef = useRef<MapRef>(null);

  const [isLoad, setIsLoad] = useState(true);
  const isRefetch = useMemo(() => {
    if (!!encryptId && !!driverId) {
      return true;
    }
    return false;
  }, [encryptId, driverId]);

  // const tempRefCount = useRef(0);
  const { data: actualMapData, isLoading: isLoadingActualMap } = useQuery({
    queryKey: ['getRealRoutePath', encryptId, driverId],
    queryFn: async () => {
      return getRealRoute({ encryptId, driverId });
      // tempRefCount.current += 1;
      // console.log('tempRefCount.current: ', tempRefCount.current);
      // return tempData.slice(0, tempRefCount.current * 100);
    },
    refetchInterval: orderStatus !== 'completed' ? 1000 * 10 : false, // 'completed'가 아닐 때 10초마다 요청
    enabled: isRefetch,
    select: data => {
      return {
        type: 'FeatureCollection',
        features: [
          {
            type: 'Feature',
            geometry: {
              type: 'LineString',
              coordinates: data ?? [],
            },
            properties: {
              type: 'Feature',
              router_type: 'real',
            },
          },
        ],
      };
    },
  });

  const currentDriverLocation = useMemo(() => {
    if (orderStatus && orderStatus === 'completed') {
      return { isShow: false, formarCordinates: [] as number[], currentCordinates: [] as number[] };
    }
    if (actualMapData) {
      if (actualMapData && actualMapData.features[0].geometry.coordinates.length === 0) {
        return { isShow: false, formarCordinates: [] as number[], currentCordinates: [] as number[] };
      } else if (actualMapData && actualMapData.features[0].geometry.coordinates.length === 1) {
        return {
          isShow: true,
          formarCordinates: [] as number[],
          currentCordinates: actualMapData.features[0].geometry.coordinates[0] as unknown as number[],
        };
      } else {
        return {
          isShow: true,
          formarCordinates: actualMapData.features[0].geometry.coordinates[
            actualMapData.features[0].geometry.coordinates.length - 2
          ] as unknown as number[],
          currentCordinates: actualMapData.features[0].geometry.coordinates[
            actualMapData.features[0].geometry.coordinates.length - 1
          ] as unknown as number[],
        };
      }
    } else {
      return { isShow: false, formarCordinates: [] as number[], currentCordinates: [] as number[] };
    }
  }, [orderStatus, actualMapData]);

  const actualLineBorderLayer: any = {
    type: 'line',
    paint: {
      'line-color': '#FFF', //점선 색상
      'line-width': 10, //점선 width
    },
  };

  const actualLineLayer: any = {
    type: 'line',
    paint: {
      'line-color': '#5d45e7', //점선 색상
      'line-width': 6, //점선 width
    },
  };

  // if (mapIsLoading && !mapData.start) return <></>;
  return (
    <MapContainer>
      <StyledMap>
        <Map
          ref={mapRef}
          style={{ width: '100%', height: '100%' }}
          localFontFamily={MapConfig.MAP_FONT_FAMILY}
          localIdeographFontFamily={MapConfig.MAP_FONT_FAMILY}
          mapboxAccessToken={MapConfig.MAP_ACCESS_TOKEN}
          mapStyle={MapConfig.MAP_STYLE}
          onLoad={() => {
            setIsLoad(false);
          }}
          initialViewState={
            destinationCordinates && {
              latitude: destinationCordinates[1],
              longitude: destinationCordinates[0],
              zoom: 14,
            }
          }
          dragRotate={true}
        >
          <MapImage />
          {!!destinationCordinates && destinationCordinates.length > 1 && (
            <Marker longitude={destinationCordinates[0]} latitude={destinationCordinates[1]} anchor="bottom">
              <CustomDiv>
                <End width={50} height={50} />
                <Span>도착</Span>
              </CustomDiv>
            </Marker>
          )}

          {!!actualMapData &&
            actualMapData.features.length > 0 &&
            actualMapData.features[0].geometry.coordinates.length > 0 &&
            actualMapData.features[0].geometry.coordinates[0] &&
            actualMapData.features[0].geometry.coordinates.length > 0 && (
              <Marker
                longitude={(actualMapData as any).features[0].geometry.coordinates[0][0]}
                latitude={(actualMapData as any).features[0].geometry.coordinates[0][1]}
              >
                <CustomDiv>
                  <Start width={50} height={50} />
                  <Span>출발</Span>
                </CustomDiv>
              </Marker>
            )}

          {currentDriverLocation.isShow && (
            <Marker
              longitude={currentDriverLocation.currentCordinates[0]}
              latitude={currentDriverLocation.currentCordinates[1]}
            >
              <MyLocation
                style={{
                  rotate: `${getBearing({
                    lat1:
                      currentDriverLocation.formarCordinates.length > 0
                        ? currentDriverLocation.formarCordinates[1]
                        : null,
                    lon1:
                      currentDriverLocation.formarCordinates.length > 0
                        ? currentDriverLocation.formarCordinates[0]
                        : null,
                    lat2: currentDriverLocation.currentCordinates[1],
                    lon2: currentDriverLocation.currentCordinates[0],
                  })}deg`,
                }}
              />
            </Marker>
          )}

          {actualMapData && (
            //@ts-ignore
            <Source id="polyline" type="geojson" data={actualMapData}>
              <Layer id="actualBorderLayer" {...actualLineBorderLayer} />
              <Layer id="actualLayer" {...actualLineLayer} />
              <Layer id="expectPinImageLayer" {...directionProps} />
            </Source>
          )}
        </Map>
      </StyledMap>
    </MapContainer>
  );
}

const Span = style.span`
  position: relative;
  top: -50px;
  left: 11px;
  width: 29.333px;
  color: #FFF;
text-align: center;

font-family: Pretendard;
font-size: 14px;
font-style: normal;
font-weight: 600;
line-height: 25px; 
letter-spacing: -0.28px;
`;

const CustomDiv = style.div`
width: 29.333px;
height: 36.667px;
flex-shrink: 0;

fill: #4D2DEF;
stroke-width: 1.5px;
stroke: var(--GrayScale-Gray100, #FFF);
filter: drop-shadow(1.5px 1.5px 3px rgba(0, 0, 0, 0.20));
`;

const MapContainer = style.div`
  position: relative;
  width: 100%;
  padding-bottom: 60%;
  height: 0;
`;

const StyledMap = style.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;
